import { IconButton } from "@dropbox/dig-components/buttons";
import {
  atoms,
  Box,
  Stack,
  ThemeContainer,
  ThemeProvider,
  useTheme,
  withShade,
} from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  CloseLine,
  FullscreenLine,
  MinusLine,
  OrgChartLine,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { FeatureLine } from "@mirage/analytics/events/enums/feature_line";
import { OrgChartViewType } from "@mirage/analytics/events/enums/org_chart_view_type";
import { PAP_Click_DashOrgChart } from "@mirage/analytics/events/types/click_dash_org_chart";
import { PAP_Click_DashProfileCard } from "@mirage/analytics/events/types/click_dash_profile_card";
import { BasicUserProfileCard } from "@mirage/mosaics/BaseProfilePage/BasicUserProfileCard";
import { ChartCard } from "@mirage/mosaics/BaseProfilePage/OrgChart/ChartCard";
import {
  adjustDeepestNodes,
  centerOnFocusedNode,
  getTreeForPerson,
  getUrl,
  isEmployee,
  nodeSize,
  padding,
  renderLinks,
  renderStyledNodes,
} from "@mirage/mosaics/BaseProfilePage/OrgChart/helpers";
import {
  useChartKeyboardShortcuts,
  useData,
} from "@mirage/mosaics/BaseProfilePage/OrgChart/hooks";
import { Title } from "@mirage/mosaics/BaseProfilePage/Title";
import {
  CollapsibleSection,
  CollapsibleSectionTitle,
} from "@mirage/mosaics/UserProfilePage/CollapsibleSection";
import { useOrgChart } from "@mirage/service-people/hooks";
import i18n from "@mirage/translations";
import cx from "classnames";
import * as d3 from "d3";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type {
  FocusData,
  HierarchyData,
  OrgChartTree,
} from "@mirage/mosaics/BaseProfilePage/OrgChart/helpers";
import type { PersonOrgChart } from "@mirage/service-people/types";
import styles from "./Chart.module.css";

export type OrgChartProps = {
  overrideTree?: HierarchyData;
  allowZoom?: boolean;
  orgChartViewType: OrgChartViewType;
  featureLine: FeatureLine;
  focus: FocusData;
  toggle: { employee?: FocusData; team?: FocusData };
  setFocus: (focus: FocusData) => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  showFullScreen?: boolean;
};

const maxZoom = 1.4;
const minZoom = 0.7;

const profileViewHeight = 186;

export const CachedOrgChart = ({
  user,
  featureLine,
  orgChartViewType,
}: {
  user: PersonOrgChart;
  featureLine: FeatureLine;
  orgChartViewType: OrgChartViewType;
}) => {
  const orgChart = useOrgChart();
  const [isOpen, setIsOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [focus, setFocus] = useState<FocusData>(user);

  useEffect(() => {
    setIsOpen(true);
    setFocus(user);
    setExpanded(false);
  }, [user.uuid]);

  if (!user.managerUuid) return null;

  return (
    <Stack>
      <Box as="div" marginBottom="12">
        <CollapsibleSectionTitle
          sectionTitle={i18n.t("organization_title")}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      </Box>
      <CollapsibleSection isOpen={isOpen}>
        <OrgChart
          overrideTree={getTreeForPerson(orgChart ?? [], focus, expanded)}
          toggle={{ employee: user }}
          featureLine={featureLine}
          orgChartViewType={orgChartViewType}
          focus={focus}
          setFocus={setFocus}
          expanded={expanded}
          setExpanded={setExpanded}
          showFullScreen={true}
        />
      </CollapsibleSection>
    </Stack>
  );
};

export const OrgChart = ({
  overrideTree,
  allowZoom = false,
  toggle,
  featureLine,
  orgChartViewType,
  focus,
  setFocus,
  expanded,
  setExpanded,
  showFullScreen,
}: OrgChartProps) => {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const { mode } = useTheme();
  const isLightMode = mode === "bright";
  const [type, setType] = useState<"employee" | "team">(
    overrideTree ? "employee" : "team",
  );

  const hoverTimeoutRef = useRef<number | undefined>();
  const ignoreFirstHover = useRef<boolean>(false);
  const [hover, setHover] = useState<FocusData | undefined>();
  const [selection, setSelection] = useState<FocusData | undefined>(focus);
  const [cardOpen, setCardOpen] = useState(false);
  const { reportPapEvent } = useMirageAnalyticsContext();

  useEffect(() => {
    if (!expanded) {
      setSelection(focus);
    }
  }, [focus, expanded]);

  const { hierarchyData } = useData({
    focus,
    type,
    selection,
    overrideTree,
    expanded,
  });

  useEffect(() => {
    if (!selection || (isEmployee(selection) && type === "team")) {
      return;
    }

    setFocus(selection);
  }, [selection, setFocus, type]);

  const [children, setChildren] = useState<number | undefined>(undefined);
  const isProfileView = !expanded && Boolean(overrideTree);
  const dotColor = isLightMode ? "#736c6450" : "#bbb5ae50";

  // Function to adjust zoom scale
  const adjustZoom = (zoomFactor: number) => {
    if (!zoomRef.current || !svgRef.current) {
      return;
    }

    const svgZoom = d3.select<SVGSVGElement, unknown>(svgRef.current);
    const point = [
      svgRef.current.clientWidth / 2,
      svgRef.current.clientHeight / 2,
    ];

    const currentTransform = d3.zoomTransform(svgZoom.node() as SVGSVGElement);
    const newScale = Math.max(
      minZoom,
      Math.min(maxZoom, currentTransform.k * zoomFactor),
    );

    // Calculate the new x and y to keep the point centered
    const zoomTransform = d3.zoomIdentity
      .translate(
        point[0] -
          (point[0] - currentTransform.x) * (newScale / currentTransform.k),
        point[1] -
          (point[1] - currentTransform.y) * (newScale / currentTransform.k),
      )
      .scale(newScale);

    svgZoom.call(zoomRef.current.transform, zoomTransform);
  };

  const recenterChart = useCallback(
    ({
      resize,
    }: {
      node?: d3.HierarchyPointNode<HierarchyData>;
      resize?: boolean;
    }) => {
      if (!zoomRef.current || !svgRef.current || !wrapperRef.current) {
        return;
      }

      if (resize) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const width = isProfileView
          ? hierarchyData?.nodes?.length *
              (nodeSize(isProfileView, expanded).width + padding) +
            padding * 1.5
          : rect.width - 8;
        svgRef.current.setAttribute("width", width + "px");
        svgRef.current.setAttribute("height", rect.height + "px");
      }

      const svgZoom = d3.select<SVGSVGElement, unknown>(svgRef.current);

      const wrapperWidth = wrapperRef.current.clientWidth;
      const width = wrapperWidth - padding * 4;
      const wrapperHeight = wrapperRef.current.clientHeight;
      const height = wrapperHeight - padding * 4;

      const pathToRoot = hierarchyData?.pathToRoot;
      const focusedNode = pathToRoot.length > 0 ? pathToRoot[0] : undefined;

      // Center on the selected node or a default position
      centerOnFocusedNode(
        svgZoom,
        zoomRef.current,
        width,
        height,
        isProfileView,
        Boolean(expanded),
        hierarchyData?.nodes?.length ?? 0,
        focusedNode,
      );
    },
    [expanded, hierarchyData?.nodes, hierarchyData?.pathToRoot, isProfileView],
  );

  // Reset zoom function
  const resetZoom = useCallback(() => {
    if (!zoomRef.current || !svgRef.current) return;

    const svgZoom = d3.select<SVGSVGElement, unknown>(svgRef.current);

    const initialTransform = d3.zoomIdentity;
    svgZoom.call(zoomRef.current.transform, initialTransform);
  }, [expanded]);

  useChartKeyboardShortcuts({ expanded, resetZoom, adjustZoom });

  useEffect(() => {
    if (!svgRef.current || !hierarchyData) return;

    const svg = d3.select<SVGSVGElement, unknown>(svgRef.current);
    svg.selectAll("*").remove();
    const g = svg.append("g");

    let currentTransform = d3.zoomIdentity;

    zoomRef.current = d3
      .zoom<SVGSVGElement, unknown>()
      .filter((event) => !["wheel", "dblclick"].includes(event.type))
      .scaleExtent([minZoom, maxZoom])
      .on("zoom", (event) => {
        currentTransform = event.transform;
        g.attr("transform", currentTransform.toString());
      });

    if (expanded || allowZoom) {
      svg.call(zoomRef.current);

      svg.on("wheel", (event) => {
        event.preventDefault();
        const point = d3.pointer(event, svg.node());

        if (event.ctrlKey || event.metaKey) {
          // Calculate the new zoom scale
          const zoomFactor = event.deltaY > 0 ? 0.96 : 1.04; // Adjusted for less intensity
          const newScale = Math.max(
            minZoom,
            Math.min(maxZoom, currentTransform.k * zoomFactor),
          );

          // Compute the new transformation centered around the mouse point
          const zoomTransform = d3.zoomIdentity
            .translate(
              point[0] -
                (point[0] - currentTransform.x) *
                  (newScale / currentTransform.k),
              point[1] -
                (point[1] - currentTransform.y) *
                  (newScale / currentTransform.k),
            )
            .scale(newScale);

          // Apply the new zoom transformation
          currentTransform = zoomTransform;
          g.attr("transform", currentTransform.toString());

          // Sync with d3.zoom's internal state
          svg.call(zoomRef.current!.transform, currentTransform);
        } else {
          // Trackpad panning behavior if Ctrl is not pressed
          const deltaX = event.deltaX;
          const deltaY = event.deltaY;
          currentTransform = currentTransform.translate(-deltaX, -deltaY);
          g.attr("transform", currentTransform.toString());

          // Sync with d3.zoom's internal state
          svg.call(zoomRef.current!.transform, currentTransform);
        }
      });
    } else {
      svg.on("wheel", null);
    }

    const { root, pathToRoot, nodes, links, maxDepth } = hierarchyData;

    const treeLayout = d3
      .tree<HierarchyData>()
      .nodeSize(
        isProfileView
          ? [
              nodeSize(isProfileView, expanded).height + padding,
              nodeSize(isProfileView, expanded).width + padding,
            ]
          : [
              nodeSize(isProfileView, expanded).width + padding,
              nodeSize(isProfileView, expanded).height + padding,
            ],
      )
      .separation(() => 1);

    treeLayout(root);

    const focusedNode = pathToRoot.length > 0 ? pathToRoot[0] : undefined;

    if (focusedNode) {
      if (focusedNode.children?.length) {
        setChildren(
          Math.ceil(
            focusedNode.children.length /
              (focusedNode.children.length > 6 ? 3 : 2),
          ),
        );
      } else {
        const parentChildren = focusedNode.parent?.children?.length ?? 0;

        focusedNode.parent && !isEmployee(focusedNode.parent.data)
          ? setChildren(
              Math.ceil(parentChildren / (parentChildren > 6 ? 3 : 2)),
            )
          : setChildren(1);
      }
    }

    if (isProfileView) {
      // Make the profile tree horizontal by flipping x and y
      root.descendants().forEach((d) => {
        const x = d.x ?? 0;
        d.x = -1 * ((d.y ?? 0) + nodeSize(isProfileView, expanded).width / 2);
        d.y = profileViewHeight / 2 - x;
      });
    } else {
      // Adjust the deepest nodes to be vertical
      adjustDeepestNodes(nodes, maxDepth, isProfileView, expanded);
    }

    // Render the lines
    renderLinks(g, links, pathToRoot, isProfileView, maxDepth, expanded);

    // Render the nodes
    renderStyledNodes(
      g,
      nodes,
      focusedNode,
      (e, node) => {
        e.stopPropagation();
        e.preventDefault();

        reportPapEvent(
          PAP_Click_DashOrgChart({
            dashProfileAction: "person",
            dashActionSurface: "dash_profile_page",
            featureLine,
            orgChartViewType,
            isExpanded: expanded,
          }),
        );

        if (expanded) {
          setSelection(node);
          setHover(node);
          ignoreFirstHover.current = true;
        } else {
          navigate(getUrl(node));
        }
      },
      (over) => {
        if (hoverTimeoutRef.current) {
          window.clearTimeout(hoverTimeoutRef.current);
        }
        if (!expanded) {
          return;
        }

        if (!over) {
          ignoreFirstHover.current = false;
        }

        if (ignoreFirstHover.current) {
          return;
        }

        if (over) {
          setHover(over);
        } else {
          hoverTimeoutRef.current = window.setTimeout(() => {
            setHover(undefined);
          }, 300);
        }
      },
      isProfileView,
      expanded,
      maxDepth,
    );

    setCardOpen(expanded);
    resetZoom();
    recenterChart({ resize: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyData]);

  useEffect(() => {
    if (!expanded) {
      setFocus(toggle.employee ?? toggle.team!);
      setSelection(toggle.employee ?? toggle.team);
      setType(toggle.employee ? "employee" : "team");
      setHover(undefined);
    }
  }, [expanded, toggle, resetZoom, setFocus]);

  return (
    <>
      {expanded && (
        <Box
          role="button"
          className={styles.overlay}
          onClick={() => setExpanded(false)}
        />
      )}
      <Box
        ref={wrapperRef}
        borderRadius="Large"
        borderStyle="Solid"
        borderColor="Border Subtle"
        borderWidth={expanded ? "1" : "0"}
        className={cx(styles.container, {
          [styles.adjustable]: expanded,
        })}
        style={{
          marginTop: 0,
          minHeight: expanded
            ? undefined
            : isProfileView
              ? profileViewHeight
              : `${padding * 3.5 + ((hierarchyData?.maxDepth ?? 0) + (children ?? 0)) * (nodeSize(isProfileView, expanded).height + padding)}px`,
          maxWidth: expanded ? "100%" : "1072px",
          backgroundImage: expanded
            ? `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`
            : undefined,
          backgroundColor: `var(--dig-color__background__${isLightMode ? "subtle" : "raised"})`,
        }}
      >
        {expanded && (
          <>
            <Box
              pointerEvents="none"
              position="absolute"
              style={{ height: 68 }}
              width="100%"
            />
            <Box position="absolute" paddingX="20" paddingY="10">
              <Title
                weightVariant="emphasized"
                size={16}
                withAccessoryStart={<UIIcon src={OrgChartLine} />}
              >
                {i18n.t("organization_title")}
              </Title>
            </Box>
          </>
        )}
        <Box
          position="absolute"
          paddingX={expanded ? "20" : "12"}
          paddingY={expanded ? "16" : "10"}
          right="0"
        >
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            {expanded && (
              <Box marginRight="16">
                <ZoomButton
                  onClick={() => {
                    reportPapEvent(
                      PAP_Click_DashOrgChart({
                        dashProfileAction: "zoom_out",
                        dashActionSurface: "dash_profile_page",
                        featureLine,
                        orgChartViewType,
                        isExpanded: expanded,
                      }),
                    );
                    adjustZoom(0.8);
                  }}
                />
                <ZoomButton
                  zoomIn
                  onClick={() => {
                    reportPapEvent(
                      PAP_Click_DashOrgChart({
                        dashProfileAction: "zoom_in",
                        dashActionSurface: "dash_profile_page",
                        featureLine,
                        orgChartViewType,
                        isExpanded: expanded,
                      }),
                    );
                    adjustZoom(1.2);
                  }}
                />
              </Box>
            )}
            {showFullScreen && (
              <ThemeProvider mode={isLightMode && expanded ? "dark" : "bright"}>
                <ThemeContainer>
                  <Box
                    as={IconButton}
                    shape="circular"
                    size="small"
                    variant={expanded ? "filled" : "outline"}
                    className={styles.fullScreenButton}
                    onClick={() => {
                      reportPapEvent(
                        PAP_Click_DashOrgChart({
                          dashProfileAction: expanded
                            ? "close_fullscreen"
                            : "fullscreen",
                          dashActionSurface: "dash_profile_page",
                          featureLine,
                          orgChartViewType,
                          isExpanded: expanded,
                        }),
                      );
                      setExpanded?.(!expanded);
                    }}
                  >
                    <UIIcon
                      src={expanded ? CloseLine : FullscreenLine}
                      className={atoms({
                        color: expanded ? "Text Base" : "Text Subtle",
                      })}
                    />
                  </Box>
                </ThemeContainer>
              </ThemeProvider>
            )}
          </Box>
        </Box>
        <ChartCard
          open={cardOpen}
          isFocus={
            (hover ?? selection ?? focus) === (toggle.employee ?? toggle.team)
          }
          expanded={Boolean(expanded)}
          onClose={() => {
            setCardOpen(false);
          }}
        >
          {isEmployee(hover ?? selection ?? focus) ? (
            <BasicUserProfileCard
              person={(hover ?? selection ?? focus) as OrgChartTree}
              onViewProfile={() => {
                reportPapEvent(
                  PAP_Click_DashProfileCard({
                    featureLine: "people",
                    profileCardType: "org_chart_card",
                  }),
                );

                setExpanded?.(false);
              }}
            />
          ) : undefined}
        </ChartCard>
        <Box className={styles.scrollable}>
          <svg ref={svgRef} width="100%" height="100%"></svg>
        </Box>
      </Box>
    </>
  );
};

const ZoomButton = ({
  zoomIn,
  onClick,
}: {
  zoomIn?: boolean;
  onClick: () => void;
}) => (
  <Box
    as="button"
    color="Text Subtle"
    backgroundColor="Background Subtle"
    borderStyle="Solid"
    borderColor="Border Subtle"
    borderWidth="1"
    paddingY="2"
    cursor="pointer"
    outlineColor="Focus Ring"
    outlineOffset="1"
    onClick={onClick}
    {...withShade({
      direction: "up",
      style: {
        height: 32,
        borderTopLeftRadius: !zoomIn ? 20 : 0,
        borderBottomLeftRadius: !zoomIn ? 20 : 0,
        borderTopRightRadius: !zoomIn ? 0 : 20,
        borderBottomRightRadius: !zoomIn ? 0 : 20,
        borderLeftWidth: !zoomIn ? 1 : 0,
        borderRightWidth: !zoomIn ? 0 : 1,
        paddingLeft: !zoomIn ? 4 : 0,
        paddingRight: !zoomIn ? 0 : 4,
      },
    })}
  >
    <UIIcon
      src={zoomIn ? AddLine : MinusLine}
      size="small"
      className={atoms({ margin: "4", marginBottom: "0" })}
    />
  </Box>
);
